<template>
    <div class="fullwidth-wrapper fullwidth-wrapper--momentum">
        <div
            :id="id"
            class="fullwidth-wrapper__content linechart"
            @mousedown="handleMouseDown"
            @mouseup="handleMouseUp"
            @mousemove="handleMouseMove"
            @mouseleave="handleMouseLeave">
        </div>
    </div>
</template>

<script>
// @ is an alias to /src

import Vue from "vue";

import { pad } from "@/utils/helpers/string.js";
import { getHighestObjectByKey } from "@/utils/helpers/array.js";

import d3 from "@/utils/d3/d3.js";

import dayjs from "dayjs";


var isoWeek = require("dayjs/plugin/isoWeek");
dayjs.extend(isoWeek);

export default {
    name: "LineChart",
    props: {
        data: {
            type: Array,
            required: true
        },
        id: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            currentWeek: dayjs(new Date()).format("YYYY-MM-DD"),
            currentYear: String(new Date().getFullYear()),
            x: 0,
            y: 0,
            top: 0,
            left: 0,
            isMouseDown: false,
            isDragEnable: false,
            isMobile: true
        };
    },
    computed: {
        screenWidth() {
            return this.$store.getters["responsiveModule/getCurrentWindowWidth"];
        },
        currentYearWeeks() {
            const weeks = this.data[0].filter((item) => {
                return item.yearweeknr.substring(0, 4) === this.currentYear;
            });

            return weeks;
        },
        selectedWeeks() {
            if (this.currentYearWeeks) {
                const currentWeekNumber = dayjs(this.currentWeek).isoWeek();

                let selectedWeeksNumber;

                if (this.isMobile) {
                    selectedWeeksNumber = [
                        pad(currentWeekNumber - 1, 2),
                        pad(currentWeekNumber, 2),
                        pad(currentWeekNumber + 1, 2),
                    ];
                } else {
                    selectedWeeksNumber = [
                        pad(currentWeekNumber - 2, 2),
                        pad(currentWeekNumber - 1, 2),
                        pad(currentWeekNumber, 2),
                        pad(currentWeekNumber + 1, 2),
                        pad(currentWeekNumber + 2, 2)
                    ];
                }

                const selectedWeeks = selectedWeeksNumber.map((item) => {


                    const currentYearWeek = `${this.currentYear}${item}`;
                    const currentWeekIndex = this.getObjetIndexByKey(this.currentYearWeeks, "yearweeknr", currentYearWeek);

                    return this.currentYearWeeks[currentWeekIndex];
                });

                return selectedWeeks;
            }
        },
        cleanedData() {
            const selectedWeeksValue = this.selectedWeeks.map((item) => {
                return item.yearweeknr;
            });

            const data = this.data.map((domain) => {
                const domainsSelectedWeeks =  domain.filter((item) => {
                    const index = selectedWeeksValue.indexOf(item.yearweeknr);

                    if (index > -1) {
                        return true;
                    }
                });

                return domainsSelectedWeeks.map((item) => {
                    return {
                        "x": {
                            "start": d3.timeParse("%Y%m%d")(item.datecalendar_first),
                            "end": d3.timeParse("%Y%m%d")(item.datecalendar_last),
                        },
                        "y": item.score,
                        "color": item.couleur,
                        "name": item.domaine
                    };
                });
            });

            return data;
        },
        artworkFlowerUrl() {
            return require("@/assets/images/artworks/colorable-flower.svg");
        },
        currentBreakpoint() {
            return this.$store.getters["responsiveModule/getCurrentBreakpoint"];
        }
    },
    watch: {
        type (newVal, oldVal) {
            if (newVal !== oldVal) {
                this.buildLifecycleChart();
            }
        },
        screenWidth(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.buildLifecycleChart();
            }
        },
        cleanedData() {
            this.buildLifecycleChart();
        }
    },
    mounted: function() {
        // Responsive Behavior
        /* istanbul ignore next */
        this.$responsive.registerMediaQuery("from-desktop", () => {
            this.isMobile = false;
        }, () => {
            this.isMobile = true;
        });

        this.buildLifecycleChart();
    },
    methods: {
        resetLifecycleChart() {
            const wrapper = document.getElementById(this.id);
            wrapper.innerHTML = "";
        },
        buildLifecycleChart() {

            Vue.nextTick(() => {
                this.resetLifecycleChart();

                const flattenedValue = this.cleanedData.map((domain) => {
                    return domain.map((item) => {
                        return (item.y) ? item.y : 0;
                    });
                }).flat();

                const yValueRange = d3.extent(flattenedValue);


                const adaptedYValueRange = [yValueRange[0], yValueRange[1] + 10];

                // Only get the current week entry for each domain
                const thisWeekDomains = this.cleanedData.map((domain) => {
                    return domain.filter((week) => {
                        if (dayjs(dayjs(this.currentWeek).isoWeek()).isSame(dayjs(week.x.start).isoWeek())) {
                            return true;
                        }
                    });
                }).flat();


                const thisWeekHighestDomain = getHighestObjectByKey(thisWeekDomains, "y");

                let columnWidth = 170;

                switch(this.currentBreakpoint) {
                case "x-small":
                    columnWidth = 75;
                    break;
                case "small":
                    columnWidth = 130;
                    break;
                case "medium":
                    columnWidth = 170;
                    break;
                }

                // set the dimensions and margins of the graph
                var margin = {top: 80, right: 30, bottom: 100, left: 40};
                // var width = (75 * (this.cleanedData.length)) + margin.left;
                var width = (columnWidth * (this.cleanedData[0].length));
                var height = 478 + margin.top + margin.bottom;

                // append the svg object to the body of the page
                var svg = d3.select("#" + this.id)
                    .append("svg")
                    .attr("preserveAspectRatio", "xMinYMin meet")
                    .attr("class", "linechart__svg")
                    .attr("width", width + 95)
                    .attr("height", height + margin.top + margin.bottom)
                    .attr("viewBox", `0 0 ${width + 75 + 10} ${height + margin.top + margin.bottom}`)
                    .append("g")
                    .attr("transform", "translate(" + margin.left + ", " + margin.top + ")");

                var x = d3.scaleLinear()
                    .domain([this.cleanedData[0][0].x.start, this.cleanedData[0][this.cleanedData[0].length - 1].x.start])
                    .range([0, width]);

                const xAxisValue = this.cleanedData[0].map((item) => {
                    return item.x.start;
                });

                const thickValue = this.cleanedData[0].map((item) => {
                    return item.x.start;
                });

                var xAxis = g => g
                    .attr("transform", `translate(0, ${height + 40})`)
                    .attr("class", "x-axis")
                    .call(d3.axisBottom(x)
                        .tickValues(thickValue)
                        .tickFormat(function(d) {
                            const startDay = d3.timeFormat("%d")(d);
                            const endDay = d3.timeFormat("%d")(dayjs(d).add(1, "week"));
                            const weekMonth = d3.timeFormat("%m")(d);
                            const weekYear = d3.timeFormat("%Y")(d);

                            var dateOutput = `${startDay}-${endDay}/${weekMonth} ${weekYear}`;

                            return dateOutput;
                        })
                    )
                    .call(g => g.select(".domain").remove());

                svg.append("g")
                    .call(xAxis);

                // Add Y axis
                var y = d3.scaleLinear()
                    .domain(adaptedYValueRange)
                    .range([ height, 0]);

                // svg.append("g")
                //     .attr("class", "y-axis")
                //     .call(d3.axisLeft(y)
                //         .ticks(this.cleanedData.length, "~s")
                //     );

                const getPointEvolution = (point) => {

                    const diff = point.diff;

                    let sign = "";

                    switch (Math.sign(diff)) {
                    case -1:
                        sign = "-";
                        break;
                    case 1:
                        sign = "+";
                        break;
                    default:
                        "";
                    }

                    const evolution = {
                        "value": String(Math.abs(diff)),
                        "sign": sign
                    };


                    // const pointIndex = this.getObjetIndexByKey(this.cleanedData, "x", point.x);

                    // let evolution = {
                    //     "value": "0",
                    //     "sign": "0"
                    // };

                    // if (pointIndex > 0) {
                    //     const previousPoint = this.cleanedData[pointIndex - 1];
                    //     const value = point.y - previousPoint.y;

                    //     let sign = "";

                    //     switch (Math.sign(value)) {
                    //     case -1:
                    //         sign = "-";
                    //         break;
                    //     case 1:
                    //         sign = "+";
                    //         break;
                    //     default:
                    //         "";
                    //     }

                    //     evolution = {
                    //         "value": Math.abs(value),
                    //         "sign": sign
                    //     };
                    // }

                    return evolution;
                };

                for (var i = this.cleanedData.length - 1; i >= 0; i--) {

                    if (this.cleanedData[i].length > 0) {
                        // Add the line
                        svg.append("path")
                            .datum(this.cleanedData[i])
                            .attr("fill", "none")
                            .attr("stroke", "#" + this.cleanedData[i][0].color)
                            .attr("stroke-width", 1.5)
                            .attr("stroke-linecap", "round")
                            .attr("stroke-dasharray", "1, 5")
                            .attr("d", d3.line().curve(d3.curveMonotoneX) // Curve the line
                                .x(function(d) {
                                    return x(d.x.start);
                                })
                                .y(function(d) {
                                    return y(d.y);
                                })
                            );

                        /* Define the data for the circles */
                        var point = svg.append("g")
                            .selectAll("dot")
                            .data(this.cleanedData[i]);

                        /*Create and place the "blocks" containing the circle and the text */
                        var pointGroup = point.enter()
                            .append("g")
                            .attr("class", (d) => {

                                let classValue = "point-wrapper";

                                if (thisWeekHighestDomain.name === d.name) {
                                    if (dayjs(dayjs(this.currentWeek).isoWeek()).isSame(dayjs(d.x.start).isoWeek())) {
                                        classValue += ` point-wrapper--active color-filter--${d.color.toLowerCase()}`;
                                    }
                                }

                                return classValue;
                            })
                            .attr("transform", function(d) {
                                return "translate("+ x(d.x.start) + ", " + y(d.y) + ")";
                            });

                        const activePoint = pointGroup.filter((d) => {
                            if (thisWeekHighestDomain.name === d.name) {
                                if (dayjs(dayjs(this.currentWeek).isoWeek()).isSame(dayjs(d.x.start).isoWeek())) {
                                    return d;
                                }
                            }
                        });

                        // Add the flower background on active point
                        activePoint.append("image")
                            .attr("x", "-91")
                            .attr("y", "-95")
                            .attr("href", this.artworkFlowerUrl)
                            .attr("width", "182")
                            .attr("height", "190")
                            .attr("class", "flower");

                        /*Create the circle for each block */
                        var circle = pointGroup.append("circle")
                            .attr("class", "point")
                            .attr("r", (d) => {
                                let pointRadius = 8;

                                // Radius for active point
                                if (thisWeekHighestDomain.name === d.name) {
                                    if (dayjs(dayjs(this.currentWeek).isoWeek()).isSame(dayjs(d.x.start).isoWeek())) {
                                        pointRadius = 0;
                                    }
                                }

                                return pointRadius;
                            })
                            .attr("stroke", (d) => {
                                return "#" + d.color;
                            })
                            .attr("stroke-width", 0)
                            .attr("fill", (d) => {
                                return "#" + d.color;
                            });
                    }
                }
            });
        },
        handleMouseUp() {
            this.isMouseDown = false;
        },
        handleMouseDown() {
            this.isMouseDown = true;
            this.y = event.pageY;
            this.x = event.pageX;
            this.top = this.$el.scrollTop;
            this.left = this.$el.scrollLeft;
        },
        handleMouseMove(event) {
            this.isDragEnable = true;
            if (this.isDragEnable && this.isMouseDown) {
                this.$el.scrollLeft = this.left - event.pageX + this.x;
                this.$el.scrollTop = this.top - event.pageY + this.y;
            }
        },
        handleMouseLeave() {
            this.isDragEnable = false;
        }
    }
};
</script>

